/**
 * API Service Layer
 * Centralized API calls for Kalamata Airport Taxi
 */

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Generic fetch wrapper with error handling
 */
async function fetchAPI(endpoint, options = {}) {
  const url = `${API_BASE}${endpoint}`;
  
  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  };

  const response = await fetch(url, config);
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }
  
  return response.json();
}

// ==========================================
// LOCATIONS
// ==========================================

export async function getLocations() {
  return fetchAPI('/locations');
}

export async function getHotels() {
  return fetchAPI('/locations/hotels');
}

export async function getPickupPoints() {
  return fetchAPI('/locations/pickup-points');
}

export async function getLocationBySlug(slug) {
  return fetchAPI(`/locations/${slug}`);
}

// ==========================================
// ROUTES
// ==========================================

export async function getAllRoutes() {
  return fetchAPI('/routes');
}

export async function getRouteBySlug(fromSlug, toSlug) {
  return fetchAPI(`/routes/by-slug?from_slug=${fromSlug}&to_slug=${toSlug}`);
}

export async function getRoutesFromLocation(fromSlug) {
  return fetchAPI(`/routes/from/${fromSlug}`);
}

export async function getPopularRoutes(limit = 6) {
  return fetchAPI(`/routes/popular?limit=${limit}`);
}

// ==========================================
// VEHICLES
// ==========================================

export async function getVehicles() {
  return fetchAPI('/vehicles');
}

// ==========================================
// BOOKINGS
// ==========================================

export async function createTransferBooking(bookingData) {
  return fetchAPI('/bookings/transfer', {
    method: 'POST',
    body: JSON.stringify(bookingData),
  });
}

export async function getBookingById(bookingId) {
  return fetchAPI(`/bookings/${bookingId}`);
}

// ==========================================
// ADMIN AUTHENTICATION
// ==========================================

export async function adminLogin(credentials) {
  return fetchAPI('/auth/login', {
    method: 'POST',
    body: JSON.stringify(credentials),
  });
}

export async function adminRegister(data) {
  return fetchAPI('/auth/register', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getAdminInfo(token) {
  return fetchAPI('/auth/me', {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });
}

// ==========================================
// ADMIN BOOKING MANAGEMENT
// ==========================================

export async function getAdminBookings(params = {}, token) {
  const searchParams = new URLSearchParams();
  if (params.booking_type) searchParams.set('booking_type', params.booking_type);
  if (params.booking_status) searchParams.set('booking_status', params.booking_status);
  if (params.limit) searchParams.set('limit', params.limit);
  
  const query = searchParams.toString();
  const endpoint = query ? `/bookings?${query}` : '/bookings';
  
  return fetchAPI(endpoint, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });
}

export async function updateBookingStatus(bookingId, update, token) {
  return fetchAPI(`/bookings/${bookingId}/status`, {
    method: 'PATCH',
    body: JSON.stringify(update),
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });
}

// ==========================================
// PAYMENTS
// ==========================================

export async function createPaymentCheckout(bookingId, paymentFor = 'full') {
  const originUrl = window.location.origin;
  return fetchAPI('/payments/checkout', {
    method: 'POST',
    body: JSON.stringify({
      booking_id: bookingId,
      origin_url: originUrl,
      payment_for: paymentFor,
    }),
  });
}

export async function getPaymentStatus(sessionId) {
  return fetchAPI(`/payments/status/${sessionId}`);
}

// ==========================================
// CUSTOMER BOOKING MANAGEMENT
// ==========================================

export async function lookupBooking(bookingId, email) {
  return fetchAPI('/bookings/lookup', {
    method: 'POST',
    body: JSON.stringify({
      booking_id: bookingId,
      email: email,
    }),
  });
}

export async function customerEditBooking(bookingId, email, editData) {
  const params = new URLSearchParams({ email });
  return fetchAPI(`/bookings/${bookingId}/customer-edit?${params}`, {
    method: 'PUT',
    body: JSON.stringify(editData),
  });
}

export async function customerCancelBooking(bookingId, email) {
  const params = new URLSearchParams({ email });
  return fetchAPI(`/bookings/${bookingId}/cancel?${params}`, {
    method: 'POST',
  });
}

// ==========================================
// UTILITY: Transform API data to match existing frontend expectations
// ==========================================

/**
 * Transform backend vehicle data to match frontend's expected format
 */
export function transformVehicle(vehicle) {
  return {
    id: vehicle.vehicle_id,
    type: vehicle.type,
    subtitle: vehicle.subtitle,
    passengers: vehicle.passengers,
    luggage: vehicle.luggage,
    hasAC: vehicle.has_ac,
    price: vehicle.base_price, // Display price, actual price comes from route.vehicle_prices
    image: vehicle.image,
    description: vehicle.description,
  };
}

/**
 * Transform backend route data to match frontend's expected format
 */
export function transformRoute(route) {
  return {
    id: route.route_id,
    from: route.from_name,
    fromSlug: route.from_slug,
    to: route.to_name,
    toSlug: route.to_slug,
    slug: route.to_slug,
    distance: route.distance,
    duration: route.duration,
    price: route.min_price || route.base_price, // Use min_price for display
  };
}
